import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import { emailTemplateService } from '../../../services/emailTemplateService';
import { emailScheduleService } from '../../../services/emailScheduleService';
import { trackCampaignGoal, trackUserBehaviorFunnel } from '../../../utils/analytics';

const SchedulingPanel = ({ template, onClose, onScheduled }) => {
  const [sendTrigger, setSendTrigger] = useState('immediate');
  const [scheduledAt, setScheduledAt] = useState('');
  const [frequency, setFrequency] = useState('daily');
  const [scheduleTime, setScheduleTime] = useState('09:00');
  const [dayOfWeek, setDayOfWeek] = useState(1);
  const [dayOfMonth, setDayOfMonth] = useState(1);
  const [recipients, setRecipients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [segmentClients, setSegmentClients] = useState([]);
  const [loadingClients, setLoadingClients] = useState(true);

  useEffect(() => {
    loadSegmentClients();
  }, [template]);

  const loadSegmentClients = async () => {
    try {
      setLoadingClients(true);
      const clients = await emailTemplateService?.getSegmentedClients(template?.segment);
      setSegmentClients(clients || []);
    } catch (error) {
      console.error('Error loading segment clients:', error);
    } finally {
      setLoadingClients(false);
    }
  };

  const handleAddRecipient = (clientId) => {
    const client = segmentClients?.find(c => c?.id === clientId);
    if (client && !recipients?.find(r => r?.id === clientId)) {
      setRecipients([...recipients, { id: client?.id, email: client?.email, name: client?.name }]);
    }
  };

  const handleRemoveRecipient = (clientId) => {
    setRecipients(recipients?.filter(r => r?.id !== clientId));
  };

  const handleSchedule = async () => {
    if (recipients?.length === 0) {
      alert('Please select at least one recipient');
      return;
    }

    try {
      setLoading(true);

      if (sendTrigger === 'immediate') {
        await emailTemplateService?.recordSend(template?.id, {
          sendTrigger: 'immediate',
          sentAt: new Date()?.toISOString(),
          recipientCount: recipients?.length,
          successCount: recipients?.length,
          failureCount: 0
        });
        
        // Track campaign goal completion
        trackCampaignGoal('email_sent_immediate', {
          goal_type: 'email_campaign_sent',
          template_id: template?.id,
          template_name: template?.templateName,
          segment: template?.segment,
          recipient_count: recipients?.length,
          send_trigger: 'immediate'
        });
        
        // Track funnel completion
        trackUserBehaviorFunnel('campaign_executed', {
          funnel_stage: 'email_sent',
          template_id: template?.id,
          recipient_count: recipients?.length
        });
        
        alert('Email sent successfully!');
      } else if (sendTrigger === 'scheduled') {
        await emailTemplateService?.recordSend(template?.id, {
          sendTrigger: 'scheduled',
          scheduledAt: scheduledAt,
          recipientCount: recipients?.length
        });
        
        // Track scheduled campaign
        trackCampaignGoal('email_scheduled', {
          goal_type: 'email_campaign_scheduled',
          template_id: template?.id,
          template_name: template?.templateName,
          segment: template?.segment,
          recipient_count: recipients?.length,
          scheduled_at: scheduledAt,
          send_trigger: 'scheduled'
        });
        
        trackUserBehaviorFunnel('campaign_scheduled', {
          funnel_stage: 'email_scheduled',
          template_id: template?.id,
          scheduled_at: scheduledAt
        });
        
        alert('Email scheduled successfully!');
      } else if (sendTrigger === 'recurring') {
        await emailScheduleService?.createSchedule({
          reportType: 'client_analytics',
          reportName: template?.templateName,
          frequency: frequency,
          scheduleTime: scheduleTime,
          dayOfWeek: frequency === 'weekly' ? dayOfWeek : null,
          dayOfMonth: frequency === 'monthly' ? dayOfMonth : null,
          status: 'active'
        });
        
        // Track recurring campaign setup
        trackCampaignGoal('recurring_campaign_created', {
          goal_type: 'recurring_campaign_setup',
          template_id: template?.id,
          template_name: template?.templateName,
          segment: template?.segment,
          frequency: frequency,
          schedule_time: scheduleTime,
          send_trigger: 'recurring'
        });
        
        trackUserBehaviorFunnel('recurring_campaign_setup', {
          funnel_stage: 'recurring_automation_created',
          template_id: template?.id,
          frequency: frequency
        });
        
        alert('Recurring schedule created successfully!');
      }

      onScheduled();
      onClose();
    } catch (error) {
      console.error('Error scheduling email:', error);
      alert('Failed to schedule email');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-card border border-border rounded-lg shadow-elevation-3 w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div>
            <h2 className="text-2xl font-heading font-bold text-foreground">
              Schedule Email
            </h2>
            <p className="text-sm text-muted-foreground mt-1">
              {template?.templateName}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-muted rounded-lg transition-smooth press-effect focus-ring"
          >
            <Icon name="X" size={24} />
          </button>
        </div>

        <div className="p-6 space-y-6">
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Send Trigger
            </label>
            <div className="grid grid-cols-3 gap-3">
              <button
                type="button"
                onClick={() => setSendTrigger('immediate')}
                className={`p-4 border rounded-lg transition-smooth ${
                  sendTrigger === 'immediate' ?'border-primary bg-primary/10 text-primary' :'border-border bg-background text-foreground hover:bg-muted'
                }`}
              >
                <Icon name="Send" size={24} className="mx-auto mb-2" />
                <div className="text-sm font-medium">Immediate</div>
              </button>
              <button
                type="button"
                onClick={() => setSendTrigger('scheduled')}
                className={`p-4 border rounded-lg transition-smooth ${
                  sendTrigger === 'scheduled' ?'border-primary bg-primary/10 text-primary' :'border-border bg-background text-foreground hover:bg-muted'
                }`}
              >
                <Icon name="Calendar" size={24} className="mx-auto mb-2" />
                <div className="text-sm font-medium">Scheduled</div>
              </button>
              <button
                type="button"
                onClick={() => setSendTrigger('recurring')}
                className={`p-4 border rounded-lg transition-smooth ${
                  sendTrigger === 'recurring' ?'border-primary bg-primary/10 text-primary' :'border-border bg-background text-foreground hover:bg-muted'
                }`}
              >
                <Icon name="Repeat" size={24} className="mx-auto mb-2" />
                <div className="text-sm font-medium">Recurring</div>
              </button>
            </div>
          </div>

          {sendTrigger === 'scheduled' && (
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Schedule Date & Time
              </label>
              <input
                type="datetime-local"
                value={scheduledAt}
                onChange={(e) => setScheduledAt(e?.target?.value)}
                className="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary bg-background text-foreground"
                required
              />
            </div>
          )}

          {sendTrigger === 'recurring' && (
            <>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Frequency
                  </label>
                  <select
                    value={frequency}
                    onChange={(e) => setFrequency(e?.target?.value)}
                    className="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary bg-background text-foreground"
                  >
                    <option value="daily">Daily</option>
                    <option value="weekly">Weekly</option>
                    <option value="monthly">Monthly</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Time
                  </label>
                  <input
                    type="time"
                    value={scheduleTime}
                    onChange={(e) => setScheduleTime(e?.target?.value)}
                    className="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary bg-background text-foreground"
                  />
                </div>
              </div>

              {frequency === 'weekly' && (
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Day of Week
                  </label>
                  <select
                    value={dayOfWeek}
                    onChange={(e) => setDayOfWeek(parseInt(e?.target?.value))}
                    className="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary bg-background text-foreground"
                  >
                    <option value={1}>Monday</option>
                    <option value={2}>Tuesday</option>
                    <option value={3}>Wednesday</option>
                    <option value={4}>Thursday</option>
                    <option value={5}>Friday</option>
                    <option value={6}>Saturday</option>
                    <option value={0}>Sunday</option>
                  </select>
                </div>
              )}

              {frequency === 'monthly' && (
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Day of Month
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="28"
                    value={dayOfMonth}
                    onChange={(e) => setDayOfMonth(parseInt(e?.target?.value))}
                    className="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary bg-background text-foreground"
                  />
                </div>
              )}
            </>
          )}

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Recipients ({recipients?.length} selected)
            </label>
            <div className="border border-border rounded-lg p-4 max-h-64 overflow-y-auto">
              {loadingClients ? (
                <div className="text-center py-4">
                  <Icon name="Loader2" size={24} className="animate-spin text-primary mx-auto" />
                </div>
              ) : (
                <div className="space-y-2">
                  {segmentClients?.map((client) => {
                    const isSelected = recipients?.find(r => r?.id === client?.id);
                    return (
                      <div
                        key={client?.id}
                        className="flex items-center justify-between p-2 hover:bg-muted rounded-lg"
                      >
                        <div>
                          <div className="font-medium text-foreground">{client?.name}</div>
                          <div className="text-sm text-muted-foreground">
                            ${client?.totalRevenue?.toFixed(2)} • {client?.serviceCount} services
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => isSelected ? handleRemoveRecipient(client?.id) : handleAddRecipient(client?.id)}
                          className={`px-3 py-1 rounded-lg text-sm transition-smooth ${
                            isSelected
                              ? 'bg-red-50 text-red-600 hover:bg-red-100' :'bg-primary/10 text-primary hover:bg-primary/20'
                          }`}
                        >
                          {isSelected ? 'Remove' : 'Add'}
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="flex gap-3 p-6 border-t border-border">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 bg-muted text-foreground rounded-lg hover:bg-muted/80 transition-smooth press-effect focus-ring"
          >
            Cancel
          </button>
          <button
            onClick={handleSchedule}
            disabled={loading || recipients?.length === 0}
            className="flex-1 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-smooth press-effect focus-ring disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <>
                <Icon name="Loader2" size={20} className="inline mr-2 animate-spin" />
                Processing...
              </>
            ) : (
              sendTrigger === 'immediate' ? 'Send Now' : 'Schedule Email'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SchedulingPanel;